import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Dropdown, IDropdown, IDropdownOption } from 'office-ui-fabric-react';

import { PersonPlaningAutonomyViewModel } from '@app/Models';
import { EnumHelpers } from '@app/Services/EnumHelpers';
import { TextCell } from './TextCell';
import { PersonPlaningAutonomyType } from '@app/AppConstants';
import { ViewMode } from './ViewMode';

interface IInlinePlaningAutonomyDropdownCellProps {
    value: PersonPlaningAutonomyType;
    row: PersonPlaningAutonomyViewModel;
    onChange: Function;
    disabled?: boolean;
}

@observer
export class InlinePlaningAutonomyDropdownCell extends React.Component<IInlinePlaningAutonomyDropdownCellProps, {}> {
    store: InlinePlaningAutonomyDropdownCellStore;

    constructor(props: IInlinePlaningAutonomyDropdownCellProps) {
        super(props);
        this.store = new InlinePlaningAutonomyDropdownCellStore(props);
    }

    componentDidUpdate(prevProps: IInlinePlaningAutonomyDropdownCellProps) {
        if (this.props.row.id !== prevProps.row.id
            || this.props.value !== prevProps.value
            || this.props.row.dateFrom !== prevProps.row.dateFrom
            || this.props.row.dateTo !== prevProps.row.dateTo
            || this.props.row.autonomyType !== prevProps.row.autonomyType) {
            this.store.updateProps(this.props);
        }
    }

    render() {
        const { mode, toEditMode, planingAutonomyOptions, onChange, ref, onMouseLeave, type } = this.store;
        const { disabled } = this.props;
        const nodes: JSX.Element[] = [];
        if (mode === ViewMode.view) {
            return <TextCell onClick={toEditMode} value={type as string} />;
        } else {
            nodes.push(
                <Dropdown key="dropdown"
                    options={planingAutonomyOptions}
                    onChange={onChange}
                    componentRef={ref}
                    onDismiss={onMouseLeave}
                    selectedKey={type as string}
                    disabled={disabled}
                />
            );
        }
        return nodes;
    }
}

type AutonomyType = 'CabinOffice' | 'CabinPlanningAutonomy' | 'CockpitKat1' | 'CockpitKat2' | 'CockpitKat3';

class InlinePlaningAutonomyDropdownCellStore {
    @observable mode: string = ViewMode.view;
    @observable type: AutonomyType;
    @observable _initType: AutonomyType;
    ref: React.RefObject<IDropdown> = React.createRef();
    private _currentProps: IInlinePlaningAutonomyDropdownCellProps;
    private readonly _entity: string = 'planingautonomy';
    prevType: AutonomyType;

    constructor(props: IInlinePlaningAutonomyDropdownCellProps) {
        this.updateProps(props);
    }

    @action
    toEditMode = () => {
        if (this._currentProps.disabled) return;
        this.mode = ViewMode.edit;
        setTimeout(() => {
            this.ref.current && this.ref.current.focus(true);
        }, 0);
    };

    @action
    onMouseLeave = () => {
        this.mode = ViewMode.view;
    };

    @action
    onChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        this.mode = ViewMode.view;
        if (option) {
            this.prevType = this.type;
            this.type = option.key as AutonomyType;
        }
        this._initType = this.type;
        this._currentProps.row.autonomyType = this.type;
        this._currentProps.onChange(this._currentProps.row, this._entity);
    };

    get planingAutonomyOptions() {
        return EnumHelpers.keys(PersonPlaningAutonomyType).map((key) => {
            return { key, text: PersonPlaningAutonomyType[key as keyof PersonPlaningAutonomyType] };
        });
    }

    @action
    updateProps(props: IInlinePlaningAutonomyDropdownCellProps) {
        this._initType = props.value as AutonomyType;
        this._currentProps = { ...props };
        this.type = props.value as AutonomyType;
    }
}
